import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FilterX, Search, X } from "lucide-react";
import {
  LotteryOptionType,
  StatusCountsType,
  StatusFilterType,
} from "../types";
import { SURFACE_STICKY } from "../styles";

type FilterBarProps = {
  search: string;
  onSearchChange: (value: string) => void;
  lotteryFilter: string;
  onLotteryChange: (value: string) => void;
  lotteryOptions: LotteryOptionType[];
  statusFilter: StatusFilterType;
  onStatusChange: (value: StatusFilterType) => void;
  counts: StatusCountsType;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
};

/**
 * Contadores são neutros por padrão. Só "Pendentes" ganha cor, e só quando há
 * algo pendente — é o único número que pede uma ação.
 */
const counterClassName = (tab: StatusFilterType, value: number) => {
  if (tab === "pending" && value > 0) {
    return "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200";
  }

  return value === 0
    ? "bg-muted text-muted-foreground/60"
    : "bg-muted text-muted-foreground";
};

const TABS: { value: StatusFilterType; label: string }[] = [
  { value: "all", label: "Todas" },
  { value: "pending", label: "Pendentes" },
  { value: "accepted", label: "Aceitas" },
  { value: "rejected", label: "Rejeitadas" },
];

export const FilterBar = ({
  search,
  onSearchChange,
  lotteryFilter,
  onLotteryChange,
  lotteryOptions,
  statusFilter,
  onStatusChange,
  counts,
  hasActiveFilters,
  onClearFilters,
}: FilterBarProps) => (
  <div className={`sticky top-0 z-20 -mx-1 flex flex-col gap-3 border-b px-1 py-3 backdrop-blur ${SURFACE_STICKY}`}>
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="relative flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Buscar por bolão, classificação, concurso ou vendedor..."
          className="pl-9 pr-9"
          aria-label="Buscar distribuições"
        />
        {search !== "" && (
          <button
            type="button"
            onClick={() => onSearchChange("")}
            aria-label="Limpar busca"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <Select value={lotteryFilter} onValueChange={onLotteryChange}>
        <SelectTrigger className="w-full sm:w-56" aria-label="Filtrar por loteria">
          <SelectValue placeholder="Todas as loterias" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todas as loterias</SelectItem>
          {lotteryOptions.map((lottery) => (
            <SelectItem key={lottery.id} value={String(lottery.id)}>
              <span className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: lottery.color || "#71717a" }}
                />
                {lottery.name}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {hasActiveFilters && (
        <Button
          variant="outline"
          onClick={onClearFilters}
          className="w-full shrink-0 sm:w-auto"
        >
          <FilterX className="mr-2 h-4 w-4" />
          Limpar filtros
        </Button>
      )}
    </div>

    <Tabs
      value={statusFilter}
      onValueChange={(value) => onStatusChange(value as StatusFilterType)}
      className="w-full"
    >
      <TabsList className="grid w-full grid-cols-4 md:max-w-xl">
        {TABS.map((tab) => (
          <TabsTrigger key={tab.value} value={tab.value} className="flex gap-1.5">
            <span className="truncate">{tab.label}</span>
            <span
              className={`rounded-full px-1.5 py-0.5 text-xs tabular-nums ${counterClassName(tab.value, counts[tab.value])}`}
            >
              {counts[tab.value]}
            </span>
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  </div>
);
